/**
 * Stage Gate Status Component
 * Shows the pass/fail status of stage gates with visual indicators
 */

import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import type { EvidenceStage, GateStatus, StageLifecycle } from '../../../api/evidence';

interface StageGateStatusProps {
  stage: EvidenceStage;
  lifecycle?: StageLifecycle;
  gateStatus?: GateStatus | null;
  compact?: boolean;
}

const STAGE_ORDER: EvidenceStage[] = ['DEVELOPMENT', 'TESTING', 'REVIEW', 'MERGE', 'COMPLETION'];

const STAGE_LABELS: Record<EvidenceStage, string> = {
  DEVELOPMENT: 'Development',
  TESTING: 'Testing',
  REVIEW: 'Review',
  MERGE: 'Merge',
  COMPLETION: 'Complete',
};

const STAGE_DESCRIPTIONS: Record<EvidenceStage, string> = {
  DEVELOPMENT: 'Code commits and changes',
  TESTING: 'Test results and coverage',
  REVIEW: 'Code review approval',
  MERGE: 'Merge and deployment',
  COMPLETION: 'Issue closed',
};

export function StageGateStatus({ stage, lifecycle, gateStatus, compact = false }: StageGateStatusProps) {
  const hasEvidence = lifecycle?.hasEvidence ?? false;
  const isAllowed = gateStatus?.allowed ?? false;
  const qualityScore = gateStatus?.qualityScore ?? lifecycle?.gateStatus?.qualityScore;

  // Determine status
  let status: 'passed' | 'failed' | 'pending' | 'not_started' = 'not_started';
  if (hasEvidence) {
    if (isAllowed || stage === 'COMPLETION') {
      status = 'passed';
    } else if (gateStatus?.reason) {
      status = 'failed';
    } else {
      status = 'pending';
    }
  }

  const statusConfig = {
    passed: {
      icon: CheckCircle,
      color: '#22C55E',
      bgColor: '#DCFCE7',
      label: 'Passed',
    },
    failed: {
      icon: XCircle,
      color: '#EF4444',
      bgColor: '#FEE2E2',
      label: 'Failed',
    },
    pending: {
      icon: Clock,
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      label: 'Pending',
    },
    not_started: {
      icon: AlertCircle,
      color: '#9CA3AF',
      bgColor: '#F3F4F6',
      label: 'Not Started',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  if (compact) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
        style={{ backgroundColor: config.bgColor, color: config.color }}
        title={`${STAGE_LABELS[stage]}: ${config.label}`}
      >
        <Icon className="w-4 h-4" />
        <span>{STAGE_LABELS[stage]}</span>
      </div>
    );
  }

  return (
    <div
      className="p-4 rounded-lg border transition-all"
      style={{
        borderColor: hasEvidence ? config.color : '#E8E0D5',
        backgroundColor: hasEvidence ? config.bgColor : 'white',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: config.color + '20', color: config.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold" style={{ color: '#2F241B' }}>
              {STAGE_LABELS[stage]}
            </h4>
            {qualityScore !== undefined && (
              <span
                className="text-sm font-medium px-2 py-0.5 rounded"
                style={{
                  backgroundColor: qualityScore >= 70 ? '#DCFCE7' : qualityScore >= 40 ? '#FEF3C7' : '#FEE2E2',
                  color: qualityScore >= 70 ? '#166534' : qualityScore >= 40 ? '#92400E' : '#991B1B',
                }}
              >
                {Math.round(qualityScore)}%
              </span>
            )}
          </div>
          <p className="text-sm mt-1" style={{ color: '#6B5D52' }}>
            {STAGE_DESCRIPTIONS[stage]}
          </p>
          {lifecycle && (
            <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: '#A39686' }}>
              <span>{lifecycle.evidenceCount} evidence items</span>
              {lifecycle.lastUpdate && (
                <span>Last updated: {new Date(lifecycle.lastUpdate).toLocaleDateString()}</span>
              )}
            </div>
          )}
          {gateStatus?.reason && status === 'failed' && (
            <p className="text-sm mt-2" style={{ color: '#EF4444' }}>
              {gateStatus.reason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stage Gate Progress Bar
 * Shows all stages in a horizontal progress view
 */
interface StageGateProgressProps {
  lifecycle: Record<string, StageLifecycle>;
  currentStage: string;
}

export function StageGateProgress({ lifecycle, currentStage }: StageGateProgressProps) {
  const currentIndex = STAGE_ORDER.indexOf(currentStage as EvidenceStage);

  return (
    <div className="flex items-center gap-2">
      {STAGE_ORDER.map((stage, index) => {
        const stageLifecycle = lifecycle[stage];
        const hasEvidence = stageLifecycle?.hasEvidence ?? false;
        const isPast = index < currentIndex;
        const isCurrent = stage === currentStage;
        const gateAllowed = stageLifecycle?.gateStatus?.allowed ?? false;

        let bgColor = '#E8E0D5'; // not started
        let textColor = '#A39686';

        if (hasEvidence && (gateAllowed || stage === 'COMPLETION')) {
          bgColor = '#22C55E'; // passed
          textColor = 'white';
        } else if (hasEvidence && !gateAllowed) {
          bgColor = '#F59E0B'; // pending/failed
          textColor = 'white';
        } else if (isPast && !hasEvidence) {
          bgColor = '#EF4444'; // missing
          textColor = 'white';
        }

        return (
          <div key={stage} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                isCurrent ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                backgroundColor: bgColor,
                color: textColor,
                ringColor: isCurrent ? '#D97F6F' : undefined,
              }}
              title={`${STAGE_LABELS[stage]}${hasEvidence ? ` (${stageLifecycle?.evidenceCount || 0} items)` : ''}`}
            >
              {index + 1}
            </div>
            {index < STAGE_ORDER.length - 1 && (
              <div
                className="w-6 h-0.5"
                style={{
                  backgroundColor: isPast || isCurrent ? '#D97F6F' : '#E8E0D5',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StageGateStatus;
